import { useEffect, useState } from "react";
import { MAIL_AUTH_LOST_EVENT, MAIL_AUTH_READY_EVENT } from "@/auth/constants";
import { isMailSessionVerified, readStoredSuiteUserId } from "@/auth/session";

export type MailAuthState = "boot" | "ready" | "lost";

function readReadyUserId(): number | null {
  const stored = readStoredSuiteUserId();
  if (!stored) return null;
  const id = Number(stored);
  return id > 0 ? id : null;
}

function initialAuthState(): MailAuthState {
  if (isMailSessionVerified()) return "ready";
  if (readStoredSuiteUserId()) return "ready";
  return "boot";
}

export function useMailAuthReady(): { state: MailAuthState; userId: number | null } {
  const [state, setState] = useState<MailAuthState>(initialAuthState);
  const [userId, setUserId] = useState<number | null>(() =>
    initialAuthState() === "ready" ? readReadyUserId() : null
  );

  useEffect(() => {
    const onReady = (e: Event) => {
      const detail = (e as CustomEvent<{ userId?: number }>).detail;
      setState("ready");
      setUserId(detail?.userId ?? readReadyUserId());
    };
    const onLost = () => {
      setState("lost");
      setUserId(null);
    };

    window.addEventListener(MAIL_AUTH_READY_EVENT, onReady);
    window.addEventListener(MAIL_AUTH_LOST_EVENT, onLost);

    if (isMailSessionVerified()) {
      setState("ready");
      setUserId(readReadyUserId());
    }

    return () => {
      window.removeEventListener(MAIL_AUTH_READY_EVENT, onReady);
      window.removeEventListener(MAIL_AUTH_LOST_EVENT, onLost);
    };
  }, []);

  return { state, userId };
}
